import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideHover } from "./useTourGuideHover";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";

/**
 * Tour Guide Hook for Admin Dashboard
 *
 * Guides admin users through:
 * - Sidebar navigation (Course, Subscriber, Journalist, FAQ, Notices, Category)
 * - Language toggler
 * - Nav control (profile)
 * - Course Management page elements (Create Course button, Search, Filters, Data Table)
 */
export const useAdminDashboardTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();

  // Detect if we're on an admin route (course management is the default landing page)
  const isOnAdminDashboard =
    pathname === ROUTE_NAMES.COURSE_MANAGEMENT ||
    pathname === ROUTE_NAMES.USER_SUBSCRIPTION ||
    pathname === ROUTE_NAMES.ADMIN_JOURNALIST_MANAGEMENT ||
    pathname === ROUTE_NAMES.FAQS ||
    pathname === ROUTE_NAMES.ADMIN_NOTICE ||
    pathname === ROUTE_NAMES.CATEGORY_MANAGEMENT;

  const isOnCourseManagement = pathname === ROUTE_NAMES.COURSE_MANAGEMENT;

  // Define tour configuration
  const config: TourGuideConfig = useMemo(
    () => {
      const baseSteps = [
        // Sidebar Steps
        {
          id: "sidebar-course",
          targetSelector: '[data-tour-item="sidebar-menu-0"]',
          title: t("tourGuide.adminDashboard.sidebarStep1.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarStep1.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-subscriber",
          targetSelector: '[data-tour-item="sidebar-menu-1"]',
          title: t("tourGuide.adminDashboard.sidebarStep2.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarStep2.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-journalist",
          targetSelector: '[data-tour-item="sidebar-menu-2"]',
          title: t("tourGuide.adminDashboard.sidebarStep3.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarStep3.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-faq",
          targetSelector: '[data-tour-item="sidebar-menu-3"]',
          title: t("tourGuide.adminDashboard.sidebarStep4.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarStep4.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-notices",
          targetSelector: '[data-tour-item="sidebar-menu-4"]',
          title: t("tourGuide.adminDashboard.sidebarStep5.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarStep5.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-category",
          targetSelector: '[data-tour-item="sidebar-menu-5"]',
          title: t("tourGuide.adminDashboard.sidebarStep6.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarStep6.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-language",
          targetSelector: '[data-tour-item="sidebar-language-toggle"]',
          title: t("tourGuide.adminDashboard.sidebarLanguage.title"),
          description: t(
            "tourGuide.adminDashboard.sidebarLanguage.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "nav-control",
          targetSelector: '[data-tour-item="admin-nav-control"]',
          title: t("tourGuide.adminDashboard.navControl.title"),
          description: t(
            "tourGuide.adminDashboard.navControl.description",
          ),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
      ];

      // Course Management Page Steps (only shown when on course management page)
      const courseManagementSteps = isOnCourseManagement
        ? [
            {
              id: "create-course-btn",
              targetSelector: '[data-tour-item="admin-create-course-btn"]',
              title: t("tourGuide.adminDashboard.step1.title"),
              description: t("tourGuide.adminDashboard.step1.description"),
              position: "bottom" as const,
              offset: { x: 0, y: 15 },
              highlightPadding: 8,
            },
            {
              id: "search-input",
              targetSelector: '[data-tour-item="admin-search-input"]',
              title: t("tourGuide.adminDashboard.step2.title"),
              description: t("tourGuide.adminDashboard.step2.description"),
              position: "bottom" as const,
              offset: { x: 0, y: 15 },
              highlightPadding: 10,
            },
            {
              id: "filters",
              targetSelector: '[data-tour-item="admin-filters"]',
              title: t("tourGuide.adminDashboard.step3.title"),
              description: t("tourGuide.adminDashboard.step3.description"),
              position: "bottom" as const,
              offset: { x: 0, y: 15 },
              highlightPadding: 10,
            },
            {
              id: "course-table",
              targetSelector: '[data-tour-item="admin-course-table"]',
              title: t("tourGuide.adminDashboard.step4.title"),
              description: t("tourGuide.adminDashboard.step4.description"),
              position: "top" as const,
              offset: { x: 0, y: -15 },
              highlightPadding: 12,
            },
          ]
        : [];

      return {
        moduleId: "adminDashboard",
        enabled: isOnAdminDashboard,
        steps: [...baseSteps, ...courseManagementSteps],
      };
    },
    [isOnAdminDashboard, isOnCourseManagement, t],
  );

  // Create base tour guide
  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.AdminDashboard, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const adminDashboardTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.AdminDashboard,
    isOnTourPath: isOnAdminDashboard,
    onTourComplete: handleTourComplete,
  });

  // Sync with tour guide context
  useEffect(() => {
    if (adminDashboardTourGuide.isActive) {
      setActiveTourModule("adminDashboard");
      setCurrentTourStep(adminDashboardTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    adminDashboardTourGuide.isActive,
    adminDashboardTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  // Create hover tooltip instances for sidebar steps
  const sidebarCourseHover = useTourGuideHover(
    "sidebar-course",
    config.steps[0],
  );
  const sidebarSubscriberHover = useTourGuideHover(
    "sidebar-subscriber",
    config.steps[1],
  );
  const sidebarJournalistHover = useTourGuideHover(
    "sidebar-journalist",
    config.steps[2],
  );
  const sidebarFaqHover = useTourGuideHover("sidebar-faq", config.steps[3]);
  const sidebarNoticesHover = useTourGuideHover(
    "sidebar-notices",
    config.steps[4],
  );
  const sidebarCategoryHover = useTourGuideHover(
    "sidebar-category",
    config.steps[5],
  );
  const sidebarLanguageHover = useTourGuideHover(
    "sidebar-language",
    config.steps[6],
  );
  const navControlHover = useTourGuideHover("nav-control", config.steps[7]);

  return {
    adminDashboardTourGuide,
    // Hover tooltip instances
    sidebarCourseHover,
    sidebarSubscriberHover,
    sidebarJournalistHover,
    sidebarFaqHover,
    sidebarNoticesHover,
    sidebarCategoryHover,
    sidebarLanguageHover,
    navControlHover,
  };
};
